import type { ReactNode } from 'react';
import { dayKeyOf, formatDayBand, formatTimeOfDay, toIso } from '../lib/formatDate';

/** The five tones an event can take. A dot and its glyph always agree. */
export type LifecycleTone = 'slate' | 'blue' | 'emerald' | 'amber' | 'red';

const DOTS: Record<LifecycleTone, string> = {
  slate: 'bg-slate-400',
  blue: 'bg-blue-500',
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

const GLYPHS: Record<LifecycleTone, string> = {
  slate: 'text-slate-400',
  blue: 'text-blue-500',
  emerald: 'text-emerald-500',
  amber: 'text-amber-500',
  red: 'text-red-500',
};

export interface LifecycleEvent {
  key: string;
  /** What happened, in the record's own vocabulary — never a coined word. */
  label: string;
  /** The instant. Events are ordered by it and grouped into day bands from it. */
  at: string;
  icon: ReactNode;
  tone?: LifecycleTone;
  /** Who did it. Absent where the system did, which is ordinary for a derived event. */
  actor?: string | null;
  /** A reason, a note, a figure — anything the event carries beyond who and when. */
  detail?: ReactNode;
}

/**
 * A record's LIFECYCLE, as a timeline — day bands, a time gutter, a dot rail, one row per
 * event.
 *
 * **Product call, 29 Sep 2026**, from the reader's own screenshot: *if the process has a
 * lifecycle like PR, apply this kind of lifecycle timeline. copy exact.* The shape is that
 * image's, band for band.
 *
 * ```
 * LIFECYCLE
 *   ─────────────────────────────── SEP 22, 2026
 *   1:01 PM   ●  Requested
 *             │
 *   ─────────────────────────────── SEP 29, 2026
 *   6:42 PM   ●  Approved
 *             │     Ari Bautista
 * ```
 *
 * **IT REVERSES A RECORDED DECISION, and the reversal is narrower than it looks.** The budget
 * plan's own section refuses a lifecycle panel outright — *"that is a second history this
 * module would have to write and keep in step with the audit trail, which already holds every
 * one of those transitions"* — and answers it with History Logs in the profile's cog. That
 * argument was about STORING a second history, and it still stands: this component stores
 * nothing. It takes events a caller DERIVES from what the record and its approval already
 * hold, so there is no second trail to fall out of step.
 *
 * <b>A caller that cannot derive its events honestly must not use this.</b> An event with an
 * invented instant is worse than an absent panel: a reader trusts a timeline about the one
 * thing it exists to state.
 *
 * **Grouped by the viewer's own calendar day** through `dayKeyOf`, so a band's label and the
 * events under it cannot disagree about which day an instant fell on.
 *
 * **Ordered by the instant, ascending** — oldest first, which is how a cycle reads. The
 * reader's screenshot shows its own source ordering events by stage rather than by date (Sep
 * 22, then Sep 30, then Sep 29), and that is the one thing here deliberately NOT copied: a
 * timeline whose bands run backwards cannot be read as a sequence, and a cycle's whole content
 * is the order things happened in.
 */
export function LifecycleTimeline({
  events,
  title = 'Lifecycle',
  empty = 'Nothing has happened to this record yet.',
  id,
}: {
  events: LifecycleEvent[];
  /**
   * The heading over the rail. `Lifecycle` unless the record calls it something else, and
   * **`null` where the SURFACE already names it** — a `<ProfileCard>` whose title is the
   * section's name draws one three lines above this, and two headings over one rail is the
   * same value in two slots. That is the ordinary case inside a card, and it is why the prop
   * is nullable rather than the heading being dropped outright: a bare call site still owes
   * the rail a name, because nothing else on screen would be giving it one.
   */
  title?: string | null;
  /** Never a blank region — an empty timeline reads as a panel that failed to load. */
  empty?: string;
  id?: string;
}) {
  /*
    SORTED HERE rather than trusted from the caller. Every caller assembles its events from
    several sources — a column, an approval, a cancellation — so the array arrives in the order
    they were written down rather than the order they happened, and a timeline is the one
    component whose entire meaning is the sequence.

    An event with an unparseable instant sorts LAST rather than being dropped: it happened, and
    a timeline silently short of a row is the shape of defect nobody notices.
  */
  const ordered = [...events].sort((a, b) => {
    const left = Date.parse(toIso(a.at) ?? '');
    const right = Date.parse(toIso(b.at) ?? '');

    if (Number.isNaN(left)) return Number.isNaN(right) ? 0 : 1;
    if (Number.isNaN(right)) return -1;

    return left - right;
  });

  return (
    <section id={id} className="flex min-w-0 flex-col gap-3">
      {/*
        The heading is chrome NAMING the region, so it takes the panel-heading treatment rather
        than the record's own — `slate-500`, uppercase and tracked, which is the step the filter
        rail's group headings and the tree's lane labels already take.
      */}
      {title != null ? (
        <h3 className="text-xs font-medium uppercase tracking-wider text-slate-500">{title}</h3>
      ) : null}

      {ordered.length === 0 ? (
        <p className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-500">
          {empty}
        </p>
      ) : (
        <ol className="flex flex-col">
          {ordered.map((event, index) => {
            const tone = event.tone ?? 'slate';
            const last = index === ordered.length - 1;
            const startsBand =
              index === 0 || dayKeyOf(event.at) !== dayKeyOf(ordered[index - 1].at);

            return (
              <li key={event.key} className="flex flex-col">
                {/*
                  THE DAY BAND — a rule taking the slack with the date at its trailing end,
                  which is the screenshot's own arrangement. Drawn only when the day CHANGES, so
                  a run of events on one afternoon reads as one group rather than as three
                  identical headings.
                */}
                {startsBand ? (
                  <div className="flex items-center gap-3 pb-2" aria-hidden="true">
                    <span className="h-px flex-1 bg-slate-200" />
                    <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
                      {formatDayBand(event.at)}
                    </span>
                  </div>
                ) : null}

                <div className="flex gap-3">
                  {/*
                    THE TIME GUTTER — fixed width and right-aligned, so the times line up down
                    their own edge whatever their length. `tabular-nums` and `mono` for the
                    reason every other machine value here has them: a time IS one.
                  */}
                  <time
                    dateTime={toIso(event.at)}
                    className="w-[4.5rem] shrink-0 pt-px text-right font-mono text-xs tabular-nums text-slate-400"
                  >
                    {formatTimeOfDay(event.at)}
                  </time>

                  {/*
                    THE RAIL — the dot, then a connector filling whatever height the content
                    needs. `flex-1` inside a column whose height is driven by the block beside it
                    is what makes the line reach the next dot without anybody measuring: a fixed
                    height falls short of a two-line event and overshoots a one-line one.

                    NOT drawn on the last event. A line running past the final dot promises a
                    row that is not coming, which on a lifecycle reads as a stage still pending.
                  */}
                  <span className="flex w-2.5 shrink-0 flex-col items-center" aria-hidden="true">
                    <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${DOTS[tone]}`} />
                    {last ? null : <span className="mt-1 w-px flex-1 bg-slate-200" />}
                  </span>

                  <div className={`min-w-0 flex-1 ${last ? '' : 'pb-5'}`}>
                    <p className="flex items-center gap-1.5 text-sm text-slate-800">
                      <span className={`shrink-0 ${GLYPHS[tone]}`} aria-hidden="true">
                        {event.icon}
                      </span>
                      {event.label}
                    </p>

                    {/*
                      THE ACTOR under the label, a step lighter — provenance for the event rather
                      than a second event. Absent where nothing did it by hand, which is ordinary
                      for a derived transition and is why this renders NOTHING rather than an em
                      dash: a dash would state that nobody was responsible.
                    */}
                    {event.actor ? (
                      <p className="mt-0.5 truncate text-xs text-slate-400">{event.actor}</p>
                    ) : null}

                    {event.detail ? (
                      <div className="mt-1 text-xs text-slate-500">{event.detail}</div>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
